import type { PortalTextKey } from "@axl/membership-sdk";

/**
 * Portal design `texts`: a school renames the interface's own words («Courses» → «Programs»). Each term
 * replaces the translation of a FIXED whitelist of loc keys — keys whose whole translation is exactly that
 * term (navigation, page titles, section headers, cards). Everything else stays localized.
 *
 * Deliberately NOT here:
 * - plural forms (`Course.plural.*`, `CourseItem.plural.*`, `Library.plural.*`): a school gives one word, not
 *   the grammatical forms; overriding only `.one` would mix «1 программа» with «3 курса». Counts keep the
 *   localized nouns.
 * - templates that contain a term («{0} steps», «Next step»): no way to rebuild them in every language.
 *
 * Add a key only when its translation is the bare term in every language.
 */
export const SCHOOL_TEXT_KEYS: Record<PortalTextKey, readonly string[]> = {
  courses: ["Courses"], // home: catalog switch + courses section title
  course: ["Course", "Global.course", "ContactLicense.type.course"], // labels, access page licence type
  libraries: ["Libraries"], // home: catalog switch + libraries section title, library header caption
  library: ["Library", "Global.library", "ContactLicense.type.library"], // library page title prefix, labels
  step: ["Step"],
  steps: ["Steps"],
  community: ["Community", "Community.title"], // navigation, community page
  achievements: ["School.nav.achievements", "Gamification.achievements", "GamificationLeader.achievements"],
  points: ["School.home.stats.points", "Gamification.points", "Gamification.transaction.points"],
  access: ["Access and payments", "School.nav.accessShort"], // navigation (full / tab bar), access page title
  feed: ["Community.feed.title"], // community: the feed's title
  newPost: ["Community.feed.writeNewPost"], // community: the «Write new post» button
  topPosts: ["Community.topPosts.title"], // community: the popular posts widget
};

/** `texts` → `{ locKey: schoolText }` for the localization layer; `null` when the school renamed nothing. */
export function buildTextOverrides(texts: Partial<Record<PortalTextKey, string>>): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const [term, keys] of Object.entries(SCHOOL_TEXT_KEYS) as [PortalTextKey, readonly string[]][]) {
    const value = texts[term];
    if (value) for (const key of keys) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}
